import type { PredictorConfig } from "./config";
import { daysBetween, isBefore, type IsoDate } from "./dates";
import type { Bean } from "./model";

/**
 * Bean degassing / aging model. Mirrors BeanAging.java.
 *
 *   effectiveAge(date) = daysBetween(roast_date, min(date, freeze_date ?? date))
 *   f(age)             = agingRatePerDay · [decafAgingMultiplier if decaf] · min(age, agingCapDays)
 *
 * Freezing stops the clock; negative ages clamp to zero.
 */
export function effectiveAgeDays(bean: Bean, date: IsoDate): number {
  let end = date;
  if (bean.freezeDate !== null && isBefore(bean.freezeDate, date)) end = bean.freezeDate;
  return Math.max(0, daysBetween(bean.roastDate, end));
}

export function agingOffset(effectiveAgeDays: number, cfg: PredictorConfig, decaf = false): number {
  const clamped = Math.max(0, Math.min(effectiveAgeDays, cfg.agingCapDays));
  const rate = cfg.agingRatePerDay * (decaf ? cfg.decafAgingMultiplier : 1.0);
  return rate * clamped;
}

/** f(age_today) − f(age_at_shot): subtract from ln(t_norm) to express the shot as it would run today. */
export function shiftToToday(bean: Bean, shotDate: IsoDate, today: IsoDate, cfg: PredictorConfig): number {
  return agingOffset(effectiveAgeDays(bean, today), cfg, bean.decaf)
       - agingOffset(effectiveAgeDays(bean, shotDate), cfg, bean.decaf);
}
